import { assert, Color } from '@hackvegas-2025/shared';

// Bloom configuration
const bloomConfig = {
  threshold: 0.9,
  intensity: 0.6,
  radius: 2.5,
  scales: [1.0, 0.8, 0.6, 0.5, 0.4], // More passes at higher resolutions
};

// Scanline configuration
const scanlineConfig = {
  intensity: 0.4, // How strong the scanlines are (0-1)
  speed: 2.0, // Animation speed
  offset: 0.0, // Phase offset
};

// Moving scanline configuration
const movingScanlineConfig = {
  intensity: 0.3, // How strong the scanlines are (0-1)
  frequency: 1.0, // Scanline frequency multiplier
  speed: 2.0, // Animation speed
  offset: 0.5, // Phase offset
};

// Hum bar configuration
const humBarConfig = {
  bars: 13.0, // Approximate number of wide bands visible across the screen height
  speed: 2.0, // Slow upward drift speed
  angleDeg: 8.0, // Angle in degrees (negative for slight upward-left to right drift)
  offset: 0.5, // Phase offset
};

// White noise configuration
const noiseConfig = {
  intensity: 0.05, // How strong the noise is (0-1)
};

// Glitch configuration (master intensity + per-type controls)
const defaultGlitchConfig = {
  intensity: 0.02, // Master 0..1 slider
  lineJitter: 0.8, // Per-scanline horizontal jitter amount
  block: 0.1, // Per-block distortion/pixelation amount
  frameJitter: 0.4, // Whole-frame horizontal jitter
  dropout: 0.3, // Horizontal band dropout intensity
  blockSize: 32.0, // Base block size in pixels (larger = chunkier)
  burstiness: 0.2, // 0..1; higher = glitches more often "on"
  speed: 0.3, // 0..1; higher = faster burst intervals
};

const glitchConfig = { ...defaultGlitchConfig };

export function slightlyIncreaseGlitchIntensity() {
  glitchConfig.intensity = 0.2;
  glitchConfig.burstiness = 0.1;
}

export function increaseGlitchIntensity() {
  glitchConfig.intensity = 0.5;
  glitchConfig.burstiness = 0.9;
  glitchConfig.speed = 0.7;
}

export function resetGlitchIntensity() {
  Object.assign(glitchConfig, defaultGlitchConfig);
}

// Curved screen configuration
const curveConfig = {
  curvature: 0.075, // How curved the screen is (0-1, 0 = flat)
  vignetteStrength: 0.2, // Darkness at edges (0-1)
  vignetteSize: 0.4, // Size of vignette effect (0-1)
  screenScale: 0.98, // Scale factor for the curved content (0-1)
  bezelColor: [0.2, 0.22, 0.26], // gray bezel color like old CRT monitors
  shadowDepth: 0.5, // Depth of shadows in crevices (0-1)
  shadowWidth: 0.05, // Width of shadow area around screen edge
  textureIntensity: 0.15, // Intensity of bezel texture (0-1)
};

const theme = {
  green: [0.64, 1.0, 0.24],
  amber: [1.0, 0.8, 0.0],
  blue: [0.5, 0.75, 1.0],
  red: [1.0, 0.5, 0.5],
  white: [1.0, 1.0, 1.0],
  cyan: [0.0, 1.0, 1.0],
  orange: [1.0, 0.65, 0.25],
};

export function createOffscreenCanvas() {
  const offscreenCanvas = new OffscreenCanvas(0, 0);
  const offscreenCtx = offscreenCanvas.getContext('2d')!;
  return {
    offscreenCanvas,
    offscreenCtx,
  };
}

type GL = WebGLRenderingContext | WebGL2RenderingContext;

// Vertex shader source (shared by all programs)
const vertexShaderSource = `
attribute vec2 a_position;
attribute vec2 a_texCoord;
varying vec2 v_texCoord;
uniform bool u_flipY;

void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
  v_texCoord = u_flipY ? vec2(a_texCoord.x, 1.0 - a_texCoord.y) : a_texCoord;
}
`;

// Base fragment shader that just blits the texture
const baseFragmentShaderSource = `
precision mediump float;
varying vec2 v_texCoord;
uniform sampler2D u_texture;

void main() {
  gl_FragColor = texture2D(u_texture, v_texCoord);
}
`;

// Bright pass shader - extracts bright pixels
const brightPassFragmentShaderSource = `
precision mediump float;
varying vec2 v_texCoord;
uniform sampler2D u_texture;
uniform float u_threshold;

void main() {
  vec4 color = texture2D(u_texture, v_texCoord);
  float luminance = dot(color.rgb, vec3(0.299, 0.587, 0.114));

  if (luminance > u_threshold) {
    gl_FragColor = color;
  } else {
    gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
  }
}
`;

// Gaussian blur shader (separable)
const blurFragmentShaderSource = `
precision mediump float;
varying vec2 v_texCoord;
uniform sampler2D u_texture;
uniform vec2 u_direction;
uniform vec2 u_resolution;
uniform float u_radius;

void main() {
  vec2 texelSize = 1.0 / u_resolution;
  vec4 color = vec4(0.0);

  // Gaussian weights for 21-tap blur (much larger radius)
  float weights[11];
  weights[0] = 0.05299;
  weights[1] = 0.05268;
  weights[2] = 0.05175;
  weights[3] = 0.05020;
  weights[4] = 0.04810;
  weights[5] = 0.04551;
  weights[6] = 0.04252;
  weights[7] = 0.03924;
  weights[8] = 0.03576;
  weights[9] = 0.03220;
  weights[10] = 0.02867;

  // Sample center
  color += texture2D(u_texture, v_texCoord) * weights[0];

  // Sample both directions with increased radius
  for(int i = 1; i < 11; i++) {
    vec2 offset = u_direction * texelSize * float(i) * u_radius;
    color += texture2D(u_texture, v_texCoord + offset) * weights[i];
    color += texture2D(u_texture, v_texCoord - offset) * weights[i];
  }

  gl_FragColor = color;
}
`;

// Final combine shader
const combineFragmentShaderSource = `
precision mediump float;
varying vec2 v_texCoord;
uniform sampler2D u_original;
uniform sampler2D u_bloom1;
uniform sampler2D u_bloom2;
uniform sampler2D u_bloom3;
uniform float u_bloomIntensity;
uniform vec3 u_tintColor;
uniform float u_scanlineIntensity;
uniform float u_scanlineFrequency;
uniform float u_noiseIntensity;
uniform float u_noisePixelSize;
uniform float u_time;
uniform vec2 u_resolution;
uniform float u_curvature;
uniform float u_vignetteStrength;
uniform float u_vignetteSize;
uniform float u_screenScale;
uniform vec3 u_bezelColor;
uniform float u_shadowDepth;
uniform float u_shadowWidth;
uniform float u_textureIntensity;
uniform float u_humIntensity;
uniform float u_humBars;
uniform float u_humSpeed;
uniform float u_humAngleDeg;
uniform float u_humOffset;
uniform float u_movingScanlineIntensity;
uniform float u_movingScanlineFrequency;
uniform float u_movingScanlineSpeed;
uniform float u_movingScanlineOffset;

// Glitch uniforms
uniform float u_glitchIntensity;    // master 0..1
uniform float u_glitchLineJitter;   // 0..1
uniform float u_glitchBlock;        // 0..1
uniform float u_glitchFrameJitter;  // 0..1
uniform float u_glitchDropout;      // 0..1
uniform float u_glitchBlockSize;    // pixels
uniform float u_glitchBurstiness;   // 0..1
uniform float u_glitchSpeed;        // 0..1

// Pseudo-random function for noise
float random(vec2 st) {
  return fract(sin(dot(st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
}

// Generate bezel texture
float bezelTexture(vec2 uv) {
  // Create fine grain texture
  float grain = random(floor(uv * 200.0)) * 0.5 + 0.5;

  // Add some larger scale variation
  float variation = random(floor(uv * 50.0)) * 0.3 + 0.7;

  // Combine for plastic-like texture
  return grain * variation;
}

// Calculate shadow based on distance from screen edge
float calculateShadow(vec2 uv, vec2 curvedCoord) {
  // Distance from screen edge
  float edgeDistX = min(curvedCoord.x, 1.0 - curvedCoord.x);
  float edgeDistY = min(curvedCoord.y, 1.0 - curvedCoord.y);
  float edgeDist = min(edgeDistX, edgeDistY);

  // Distance from original UV edge
  float uvEdgeDistX = min(uv.x, 1.0 - uv.x);
  float uvEdgeDistY = min(uv.y, 1.0 - uv.y);
  float uvEdgeDist = min(uvEdgeDistX, uvEdgeDistY);

  // Create shadow in crevice area
  float shadowFactor = 1.0;
  if (edgeDist < 0.0) {
    // We're in the bezel area, check if we're near the screen edge
    float distFromScreenEdge = -edgeDist;
    if (distFromScreenEdge < u_shadowWidth) {
      shadowFactor = mix(1.0 - u_shadowDepth, 1.0, distFromScreenEdge / u_shadowWidth);
    }
  }

  return shadowFactor;
}

// Apply barrel distortion for curved screen effect
vec2 curveScreen(vec2 uv) {
  // Center the coordinates
  uv = uv * 2.0 - 1.0;

  // Apply barrel distortion
  float r2 = dot(uv, uv);
  float distortion = 1.0 + u_curvature * r2;
  uv *= distortion;

  // Scale and recenter
  uv = (uv * u_screenScale + 1.0) * 0.5;

  return uv;
}

void main() {
  // Apply curve distortion to texture coordinates
  vec2 curvedCoord = curveScreen(v_texCoord);

  // Check if we're outside the curved screen bounds
  if (curvedCoord.x < 0.0 || curvedCoord.x > 1.0 || curvedCoord.y < 0.0 || curvedCoord.y > 1.0) {
    // We're in the bezel area - apply texture and shadows
    vec3 bezelColor = u_bezelColor;

    // Add texture to bezel
    float texture = bezelTexture(v_texCoord);
    bezelColor *= mix(1.0, texture, u_textureIntensity);

    // Add shadow effects
    float shadow = calculateShadow(v_texCoord, curvedCoord);
    bezelColor *= shadow;

    gl_FragColor = vec4(bezelColor, 1.0);
    return;
  }

  // Glitch modulation (rare, extreme bursts)
  float intervalHz = mix(0.15, 0.6, clamp(u_glitchSpeed, 0.0, 1.0));
  float randBurst = random(vec2(floor(u_time * intervalHz), 313.37));
  float burstOn = step(1.0 - clamp(u_glitchBurstiness, 0.0, 1.0), randBurst);
  float tIn = fract(u_time * intervalHz);
  float burstShape = smoothstep(0.0, 0.12, tIn) * (1.0 - smoothstep(0.4, 1.0, tIn)); // fast in, fast out
  float g = clamp(u_glitchIntensity, 0.0, 1.0) * burstOn * (0.6 + 0.6 * burstShape); // baseline 0 when off

  // Build a glitched sampling coordinate (content-space). Screen-space CRT effects remain anchored.
  vec2 glitchCoord = curvedCoord;

  // Per-scanline horizontal jitter (huge during peaks)
  float gy = curvedCoord.y * u_resolution.y;
  float jitterSeed = random(vec2(floor(gy), floor(u_time * (20.0 + 100.0 * burstOn))));
  float lineShift = (jitterSeed - 0.5) * ((16.0 + 80.0 * burstShape) / u_resolution.x) * u_glitchLineJitter * g;

  // Whole-frame jitter
  float frameSeed = random(vec2(floor(u_time * 30.0), 12.0));
  float frameShift = (frameSeed - 0.5) * ((6.0 + 32.0 * burstShape) / u_resolution.x) * u_glitchFrameJitter * g;

  // Block glitch (offset + optional pixelation)
  float blockPx = max(4.0, u_glitchBlockSize * (1.2 - 0.8 * g));
  vec2 blockIndex = floor(curvedCoord * u_resolution / blockPx);
  float blockRand = random(blockIndex + floor(u_time * (6.0 + 24.0 * burstOn)));
  float blockToggle = step(mix(0.95, 0.6, burstShape), blockRand); // many more blocks at peaks
  vec2 blockOffset = (blockRand - 0.5) * vec2((24.0 + 160.0 * burstShape) / u_resolution.x, (4.0 + 24.0 * burstShape) / u_resolution.y) * u_glitchBlock * g;

  // Slice-based tearing (rolling horizontal bands and noisy bins)
  float slicePhase = u_time * (0.25 + 1.75 * burstOn);
  float sliceCenter = fract(slicePhase * 0.23 + 0.1 * sin(slicePhase * 3.3));
  float sliceHeight = mix(0.02, 0.15, burstShape);
  float sliceMask = smoothstep(sliceCenter - sliceHeight, sliceCenter, curvedCoord.y) * (1.0 - smoothstep(sliceCenter, sliceCenter + sliceHeight, curvedCoord.y));
  float ybin = floor(gy / max(4.0, blockPx));
  float sliceNoise = random(vec2(ybin, floor(u_time * 10.0)));
  float binSlice = step(0.92, sliceNoise) * burstOn;

  // Apply accumulated offsets
  float tearShift = (sliceMask + binSlice) * (random(vec2(ybin, 99.0)) - 0.5) * ((40.0 + 140.0 * burstShape) / u_resolution.x) * g;
  glitchCoord += vec2(lineShift + frameShift + tearShift, 0.0);
  glitchCoord += blockOffset * blockToggle;
  glitchCoord.y += (sliceMask) * (random(vec2(ybin, 123.0)) - 0.5) * (0.01 + 0.04 * burstShape) * g;
  glitchCoord = clamp(glitchCoord, vec2(0.0), vec2(1.0));

  // Pixel-sorting approximation within active slices (horizontal smear toward local maxima)
  float sortAmt = (sliceMask + binSlice) * g * u_glitchBlock;
  float dx = (blockPx * (1.0 + 3.0 * burstShape)) / u_resolution.x;
  vec2 uvL = clamp(glitchCoord + vec2(-dx, 0.0), 0.0, 1.0);
  vec2 uvC = glitchCoord;
  vec2 uvR = clamp(glitchCoord + vec2(dx, 0.0), 0.0, 1.0);

  vec4 sL = texture2D(u_original, uvL);
  vec4 sC = texture2D(u_original, uvC);
  vec4 sR = texture2D(u_original, uvR);
  float lL = dot(sL.rgb, vec3(0.299, 0.587, 0.114));
  float lC = dot(sC.rgb, vec3(0.299, 0.587, 0.114));
  float lR = dot(sR.rgb, vec3(0.299, 0.587, 0.114));
  vec4 max1 = mix(sL, sC, step(lL, lC));
  float lm1 = max(lL, lC);
  vec4 max2 = mix(max1, sR, step(lm1, lR));
  vec4 original = mix(sC, max2, sortAmt);

  vec4 originalPreTint = original;

  // Optional pixelation mix per block (for a compression/block-glitch feel)
  vec2 pixUV = (blockIndex + 0.5) * blockPx / u_resolution;
  vec4 originalPix = texture2D(u_original, pixUV);

  // Pixel-sorting on bloom textures so glow follows the smear
  vec4 b1L = texture2D(u_bloom1, uvL);
  vec4 b1C = texture2D(u_bloom1, uvC);
  vec4 b1R = texture2D(u_bloom1, uvR);
  float bl1L = dot(b1L.rgb, vec3(0.299, 0.587, 0.114));
  float bl1C = dot(b1C.rgb, vec3(0.299, 0.587, 0.114));
  float bl1R = dot(b1R.rgb, vec3(0.299, 0.587, 0.114));
  vec4 b1m1 = mix(b1L, b1C, step(bl1L, bl1C));
  float b1lm1 = max(bl1L, bl1C);
  vec4 b1m2 = mix(b1m1, b1R, step(b1lm1, bl1R));
  vec4 bloom1 = mix(b1C, b1m2, sortAmt);

  vec4 b2L = texture2D(u_bloom2, uvL);
  vec4 b2C = texture2D(u_bloom2, uvC);
  vec4 b2R = texture2D(u_bloom2, uvR);
  float bl2L = dot(b2L.rgb, vec3(0.299, 0.587, 0.114));
  float bl2C = dot(b2C.rgb, vec3(0.299, 0.587, 0.114));
  float bl2R = dot(b2R.rgb, vec3(0.299, 0.587, 0.114));
  vec4 b2m1 = mix(b2L, b2C, step(bl2L, bl2C));
  float b2lm1 = max(bl2L, bl2C);
  vec4 b2m2 = mix(b2m1, b2R, step(b2lm1, bl2R));
  vec4 bloom2 = mix(b2C, b2m2, sortAmt);

  vec4 b3L = texture2D(u_bloom3, uvL);
  vec4 b3C = texture2D(u_bloom3, uvC);
  vec4 b3R = texture2D(u_bloom3, uvR);
  float bl3L = dot(b3L.rgb, vec3(0.299, 0.587, 0.114));
  float bl3C = dot(b3C.rgb, vec3(0.299, 0.587, 0.114));
  float bl3R = dot(b3R.rgb, vec3(0.299, 0.587, 0.114));
  vec4 b3m1 = mix(b3L, b3C, step(bl3L, bl3C));
  float b3lm1 = max(bl3L, bl3C);
  vec4 b3m2 = mix(b3m1, b3R, step(b3lm1, bl3R));
  vec4 bloom3 = mix(b3C, b3m2, sortAmt);

  vec4 bloom1Pix = texture2D(u_bloom1, pixUV);
  vec4 bloom2Pix = texture2D(u_bloom2, pixUV);
  vec4 bloom3Pix = texture2D(u_bloom3, pixUV);

  float blockMix = blockToggle * u_glitchBlock * g;
  original = mix(original, originalPix, blockMix);
  bloom1 = mix(bloom1, bloom1Pix, blockMix);
  bloom2 = mix(bloom2, bloom2Pix, blockMix);
  bloom3 = mix(bloom3, bloom3Pix, blockMix);

  // Wide diagonal hum bars (rolling bars) applied to base/background only (before tint and before bloom)
  float angle = radians(u_humAngleDeg);
  vec2 dir = normalize(vec2(sin(angle), cos(angle)));
  float coord = dot(glitchCoord * u_resolution, dir);
  // 2*pi * bars / screenHeight gives bands across height even when angled
  float k = 6.28318530718 * u_humBars / u_resolution.y;
  float bars = sin(coord * k + u_time * u_humSpeed + u_humOffset);
  float x = bars * u_humIntensity;
  float softX = x / (1.0 + abs(x) * 1.5);
  float humFactor = 1.0 - softX;
  // Mask using pre-tint original luminance
  float baseLuma = dot(originalPreTint.rgb, vec3(0.299, 0.587, 0.114));
  float textMask = smoothstep(0.05, 0.2, baseLuma);
  float backgroundMask = 1.0 - textMask;
  // Apply hum to base only
  original.rgb = mix(original.rgb, original.rgb * humFactor, backgroundMask);

  // Apply tint to original (multiply white text by tint color)
  original.rgb *= u_tintColor;

  // Apply tint to bloom as well
  bloom1.rgb *= u_tintColor;
  bloom2.rgb *= u_tintColor;
  bloom3.rgb *= u_tintColor;

  vec4 bloom = (bloom1 + bloom2 + bloom3) * u_bloomIntensity;
  vec4 finalColor = original + bloom;

  // Apply white noise before scanline effect
  vec2 noiseCoord = floor(curvedCoord * u_resolution / u_noisePixelSize) * u_noisePixelSize / u_resolution;
  float noise = random(noiseCoord + u_time * 0.1) * 2.0 - 1.0;
  finalColor.rgb += noise * u_noiseIntensity;

  finalColor.rgb *= u_tintColor;

  // Occasional dropout bands (aggressive during bursts)
  float bandSeed = random(vec2(floor(gy / 8.0), floor(u_time * (8.0 + 30.0 * burstOn))));
  float dropout = step(0.97, bandSeed) * u_glitchDropout * g * (0.5 + 0.5 * (sliceMask + binSlice));
  finalColor.rgb = mix(finalColor.rgb, finalColor.rgb * 0.0, dropout);

  // Apply scanline effect using curved coordinates - aligned with noise pixels
  // Use the same pixel grid as the noise
  float pixelY = curvedCoord.y * u_resolution.y;
  // Create a smooth scanline for each pixel row using sine wave (inverted - dark edges, bright centers)
  float scanlinePhase = mod(pixelY, u_noisePixelSize) / u_noisePixelSize;
  // Sharpen the sine wave by raising it to a power, creating larger, darker gaps
  float sineValue = sin(scanlinePhase * 3.14159265359) * 0.5 + 0.5;
  sineValue = pow(sineValue, 2.5); // Sharpen the curve for more pronounced gaps
  float scanline = 1.0 - sineValue;
  float scanlineFactor = 1.0 - (scanline * u_scanlineIntensity * 1.5); // Amplify the effect

  // Apply subtle horizontal fade for more realistic CRT effect
  float horizontalFade = sin(curvedCoord.y * 3.14159) * 0.1 + 0.9;
  scanlineFactor *= horizontalFade;

  // Moving scanlines over curved coordinates
  float scanlineY = curvedCoord.y * u_resolution.y * u_movingScanlineFrequency;
  float movingScan = sin(scanlineY + (u_time * u_movingScanlineSpeed) + u_movingScanlineOffset) * 0.5 + 0.5;
  float m = movingScan * 2.0 - 1.0;
  float movingAmp = u_movingScanlineIntensity;
  float movingScanFactor = clamp(1.0 - m * movingAmp, 0.6, 1.25);

  // Zero-mean moving scanlines; multiply with pixel-aligned scanlines
  finalColor.rgb *= scanlineFactor * movingScanFactor;

  // Apply vignette effect
  vec2 vignetteCoord = v_texCoord * 2.0 - 1.0;
  float vignette = 1.0 - smoothstep(u_vignetteSize, 1.0, length(vignetteCoord));
  vignette = mix(1.0 - u_vignetteStrength, 1.0, vignette);
  finalColor.rgb *= vignette;

  gl_FragColor = finalColor;
}
`;

function createShader(gl: GL, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  assert(shader !== null);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  const status = gl.getShaderParameter(shader, gl.COMPILE_STATUS);
  if (!status) {
    gl.deleteShader(shader);
    throw new Error('Failed to compile shader');
  }

  return shader;
}

function createProgram(gl: GL, vertexShader: WebGLShader, fragmentShader: WebGLShader): WebGLProgram {
  const program = gl.createProgram();
  assert(program !== null);
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);

  const status = gl.getProgramParameter(program, gl.LINK_STATUS);
  if (!status) {
    gl.deleteProgram(program);
    throw new Error('Failed to link program');
  }

  return program;
}

function createTexture(gl: GL, width: number, height: number): WebGLTexture {
  const texture = gl.createTexture();
  assert(texture !== null);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return texture;
}

function createFramebuffer(gl: GL, texture: WebGLTexture): WebGLFramebuffer {
  const framebuffer = gl.createFramebuffer();
  assert(framebuffer !== null);
  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);

  const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
  if (status !== gl.FRAMEBUFFER_COMPLETE) {
    throw new Error('Framebuffer not complete');
  }

  return framebuffer;
}

type Programs = {
  vertexShader: WebGLShader;
  baseProgram: WebGLProgram;
  brightPassProgram: WebGLProgram;
  blurProgram: WebGLProgram;
  combineProgram: WebGLProgram;
};

type BrightPassUniforms = {
  texture: WebGLUniformLocation;
  threshold: WebGLUniformLocation;
  flipY: WebGLUniformLocation;
};

type BlurUniforms = {
  texture: WebGLUniformLocation;
  direction: WebGLUniformLocation;
  resolution: WebGLUniformLocation;
  radius: WebGLUniformLocation;
  flipY: WebGLUniformLocation;
};

type CombineUniforms = {
  original: WebGLUniformLocation;
  bloom1: WebGLUniformLocation;
  bloom2: WebGLUniformLocation;
  bloom3: WebGLUniformLocation;
  bloomIntensity: WebGLUniformLocation;
  tintColor: WebGLUniformLocation;
  scanlineIntensity: WebGLUniformLocation;
  scanlineFrequency: WebGLUniformLocation;
  noiseIntensity: WebGLUniformLocation;
  noisePixelSize: WebGLUniformLocation;
  time: WebGLUniformLocation;
  resolution: WebGLUniformLocation;
  curvature: WebGLUniformLocation;
  vignetteStrength: WebGLUniformLocation;
  vignetteSize: WebGLUniformLocation;
  screenScale: WebGLUniformLocation;
  bezelColor: WebGLUniformLocation;
  shadowDepth: WebGLUniformLocation;
  shadowWidth: WebGLUniformLocation;
  textureIntensity: WebGLUniformLocation;
  movingScanlineIntensity: WebGLUniformLocation;
  movingScanlineFrequency: WebGLUniformLocation;
  movingScanlineSpeed: WebGLUniformLocation;
  movingScanlineOffset: WebGLUniformLocation;
  humIntensity: WebGLUniformLocation;
  humBars: WebGLUniformLocation;
  humSpeed: WebGLUniformLocation;
  humAngleDeg: WebGLUniformLocation;
  humOffset: WebGLUniformLocation;

  // Glitch
  glitchIntensity: WebGLUniformLocation;
  glitchLineJitter: WebGLUniformLocation;
  glitchBlock: WebGLUniformLocation;
  glitchFrameJitter: WebGLUniformLocation;
  glitchDropout: WebGLUniformLocation;
  glitchBlockSize: WebGLUniformLocation;
  glitchBurstiness: WebGLUniformLocation;
  glitchSpeed: WebGLUniformLocation;
};

type Attributes = {
  positionAttributeLocation: number;
  texCoordAttributeLocation: number;
};

type GeometryBuffers = {
  positionBuffer: WebGLBuffer;
  texCoordBuffer: WebGLBuffer;
};

export function initPrograms(gl: GL): {
  programs: Programs;
  uniforms: { bright: BrightPassUniforms; blur: BlurUniforms; combine: CombineUniforms };
} {
  const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);

  const baseProgram = createProgram(gl, vertexShader, createShader(gl, gl.FRAGMENT_SHADER, baseFragmentShaderSource));
  const brightPassProgram = createProgram(
    gl,
    vertexShader,
    createShader(gl, gl.FRAGMENT_SHADER, brightPassFragmentShaderSource),
  );
  const blurProgram = createProgram(gl, vertexShader, createShader(gl, gl.FRAGMENT_SHADER, blurFragmentShaderSource));
  const combineProgram = createProgram(
    gl,
    vertexShader,
    createShader(gl, gl.FRAGMENT_SHADER, combineFragmentShaderSource),
  );

  const brightUniforms: BrightPassUniforms = {
    texture: gl.getUniformLocation(brightPassProgram, 'u_texture')!,
    threshold: gl.getUniformLocation(brightPassProgram, 'u_threshold')!,
    flipY: gl.getUniformLocation(brightPassProgram, 'u_flipY')!,
  };

  const blurUniforms: BlurUniforms = {
    texture: gl.getUniformLocation(blurProgram, 'u_texture')!,
    direction: gl.getUniformLocation(blurProgram, 'u_direction')!,
    resolution: gl.getUniformLocation(blurProgram, 'u_resolution')!,
    radius: gl.getUniformLocation(blurProgram, 'u_radius')!,
    flipY: gl.getUniformLocation(blurProgram, 'u_flipY')!,
  };

  const combineUniforms: CombineUniforms = {
    original: gl.getUniformLocation(combineProgram, 'u_original')!,
    bloom1: gl.getUniformLocation(combineProgram, 'u_bloom1')!,
    bloom2: gl.getUniformLocation(combineProgram, 'u_bloom2')!,
    bloom3: gl.getUniformLocation(combineProgram, 'u_bloom3')!,
    bloomIntensity: gl.getUniformLocation(combineProgram, 'u_bloomIntensity')!,
    tintColor: gl.getUniformLocation(combineProgram, 'u_tintColor')!,
    scanlineIntensity: gl.getUniformLocation(combineProgram, 'u_scanlineIntensity')!,
    scanlineFrequency: gl.getUniformLocation(combineProgram, 'u_scanlineFrequency')!,
    noiseIntensity: gl.getUniformLocation(combineProgram, 'u_noiseIntensity')!,
    noisePixelSize: gl.getUniformLocation(combineProgram, 'u_noisePixelSize')!,
    time: gl.getUniformLocation(combineProgram, 'u_time')!,
    resolution: gl.getUniformLocation(combineProgram, 'u_resolution')!,
    curvature: gl.getUniformLocation(combineProgram, 'u_curvature')!,
    vignetteStrength: gl.getUniformLocation(combineProgram, 'u_vignetteStrength')!,
    vignetteSize: gl.getUniformLocation(combineProgram, 'u_vignetteSize')!,
    screenScale: gl.getUniformLocation(combineProgram, 'u_screenScale')!,
    bezelColor: gl.getUniformLocation(combineProgram, 'u_bezelColor')!,
    shadowDepth: gl.getUniformLocation(combineProgram, 'u_shadowDepth')!,
    shadowWidth: gl.getUniformLocation(combineProgram, 'u_shadowWidth')!,
    textureIntensity: gl.getUniformLocation(combineProgram, 'u_textureIntensity')!,
    movingScanlineIntensity: gl.getUniformLocation(combineProgram, 'u_movingScanlineIntensity')!,
    movingScanlineFrequency: gl.getUniformLocation(combineProgram, 'u_movingScanlineFrequency')!,
    movingScanlineSpeed: gl.getUniformLocation(combineProgram, 'u_movingScanlineSpeed')!,
    movingScanlineOffset: gl.getUniformLocation(combineProgram, 'u_movingScanlineOffset')!,
    humIntensity: gl.getUniformLocation(combineProgram, 'u_humIntensity')!,
    humBars: gl.getUniformLocation(combineProgram, 'u_humBars')!,
    humSpeed: gl.getUniformLocation(combineProgram, 'u_humSpeed')!,
    humAngleDeg: gl.getUniformLocation(combineProgram, 'u_humAngleDeg')!,
    humOffset: gl.getUniformLocation(combineProgram, 'u_humOffset')!,

    // Glitch
    glitchIntensity: gl.getUniformLocation(combineProgram, 'u_glitchIntensity')!,
    glitchLineJitter: gl.getUniformLocation(combineProgram, 'u_glitchLineJitter')!,
    glitchBlock: gl.getUniformLocation(combineProgram, 'u_glitchBlock')!,
    glitchFrameJitter: gl.getUniformLocation(combineProgram, 'u_glitchFrameJitter')!,
    glitchDropout: gl.getUniformLocation(combineProgram, 'u_glitchDropout')!,
    glitchBlockSize: gl.getUniformLocation(combineProgram, 'u_glitchBlockSize')!,
    glitchBurstiness: gl.getUniformLocation(combineProgram, 'u_glitchBurstiness')!,
    glitchSpeed: gl.getUniformLocation(combineProgram, 'u_glitchSpeed')!,
  };

  return {
    programs: { vertexShader, baseProgram, brightPassProgram, blurProgram, combineProgram },
    uniforms: { bright: brightUniforms, blur: blurUniforms, combine: combineUniforms },
  };
}

export function initGeometry(gl: GL, baseProgram: WebGLProgram): { attributes: Attributes; buffers: GeometryBuffers } {
  const positionAttributeLocation = gl.getAttribLocation(baseProgram, 'a_position');
  const texCoordAttributeLocation = gl.getAttribLocation(baseProgram, 'a_texCoord');

  const positionBuffer = gl.createBuffer();
  assert(positionBuffer !== null);
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  const positions = [-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1];
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);

  const texCoordBuffer = gl.createBuffer();
  assert(texCoordBuffer !== null);
  gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
  const texCoords = [0, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1, 0];
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(texCoords), gl.STATIC_DRAW);

  return {
    attributes: { positionAttributeLocation, texCoordAttributeLocation },
    buffers: { positionBuffer, texCoordBuffer },
  };
}

function setupVertexAttributes(gl: GL, attributes: Attributes, buffers: GeometryBuffers) {
  gl.enableVertexAttribArray(attributes.positionAttributeLocation);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffers.positionBuffer);
  gl.vertexAttribPointer(attributes.positionAttributeLocation, 2, gl.FLOAT, false, 0, 0);

  gl.enableVertexAttribArray(attributes.texCoordAttributeLocation);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffers.texCoordBuffer);
  gl.vertexAttribPointer(attributes.texCoordAttributeLocation, 2, gl.FLOAT, false, 0, 0);
}

function renderFullscreenQuad(gl: GL, attributes: Attributes, buffers: GeometryBuffers) {
  setupVertexAttributes(gl, attributes, buffers);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
}

type BloomResources = {
  originalTexture: WebGLTexture;
  brightTexture: WebGLTexture;
  brightFramebuffer: WebGLFramebuffer;
  blurTextures: WebGLTexture[];
  blurFramebuffers: WebGLFramebuffer[];
  tempTextures: WebGLTexture[];
  tempFramebuffers: WebGLFramebuffer[];
  width: number;
  height: number;
};

export function createBloomResourcesManager(gl: GL) {
  let bloomResources: BloomResources | null = null;

  const dispose = () => {
    if (!bloomResources) return;
    gl.deleteTexture(bloomResources.originalTexture);
    gl.deleteTexture(bloomResources.brightTexture);
    gl.deleteFramebuffer(bloomResources.brightFramebuffer);
    bloomResources.blurTextures.forEach((tex) => gl.deleteTexture(tex));
    bloomResources.blurFramebuffers.forEach((fb) => gl.deleteFramebuffer(fb));
    bloomResources.tempTextures.forEach((tex) => gl.deleteTexture(tex));
    bloomResources.tempFramebuffers.forEach((fb) => gl.deleteFramebuffer(fb));
  };

  const ensure = (width: number, height: number): BloomResources => {
    if (bloomResources && bloomResources.width === width && bloomResources.height === height) {
      return bloomResources;
    }

    if (bloomResources) {
      dispose();
    }

    const originalTexture = createTexture(gl, width, height);

    const brightTexture = createTexture(gl, width, height);
    const brightFramebuffer = createFramebuffer(gl, brightTexture);

    const blurTextures: WebGLTexture[] = [];
    const blurFramebuffers: WebGLFramebuffer[] = [];
    const tempTextures: WebGLTexture[] = [];
    const tempFramebuffers: WebGLFramebuffer[] = [];

    for (let i = 1; i < bloomConfig.scales.length; i++) {
      const scale = bloomConfig.scales[i]!;
      const scaledWidth = Math.max(1, Math.floor(width * scale));
      const scaledHeight = Math.max(1, Math.floor(height * scale));

      const blurTexture = createTexture(gl, scaledWidth, scaledHeight);
      const blurFramebuffer = createFramebuffer(gl, blurTexture);
      blurTextures.push(blurTexture);
      blurFramebuffers.push(blurFramebuffer);

      const tempTexture = createTexture(gl, scaledWidth, scaledHeight);
      const tempFramebuffer = createFramebuffer(gl, tempTexture);
      tempTextures.push(tempTexture);
      tempFramebuffers.push(tempFramebuffer);
    }

    bloomResources = {
      originalTexture,
      brightTexture,
      brightFramebuffer,
      blurTextures,
      blurFramebuffers,
      tempTextures,
      tempFramebuffers,
      width,
      height,
    };

    return bloomResources;
  };

  return { ensure };
}

export type ShaderData = {
  programs: Programs;
  uniforms: { bright: BrightPassUniforms; blur: BlurUniforms; combine: CombineUniforms };
  attributes: Attributes;
  buffers: GeometryBuffers;
  bloomManager: { ensure: (width: number, height: number) => BloomResources };
  offscreenCanvas: OffscreenCanvas;
  offscreenCtx: OffscreenCanvasRenderingContext2D;
};

export function setupWebglCanvas(offscreenCanvas: OffscreenCanvas, offscreenCtx: OffscreenCanvasRenderingContext2D) {
  const canvas = document.createElement('canvas');

  const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
  assert(gl !== null);

  const { programs, uniforms } = initPrograms(gl);
  const { attributes, buffers } = initGeometry(gl, programs.baseProgram);
  const bloomManager = createBloomResourcesManager(gl);

  const shaderData: ShaderData = {
    programs,
    uniforms,
    attributes,
    buffers,
    bloomManager,
    offscreenCanvas,
    offscreenCtx,
  };

  return { canvas, gl, shaderData };
}

export function drawWithShaders(
  gl: GL,
  webglCanvas: HTMLCanvasElement,
  shaderData: ShaderData,
  renderTerminalToOffscreen: (
    canvas: OffscreenCanvas,
    ctx: OffscreenCanvasRenderingContext2D,
    webglCanvas: HTMLCanvasElement,
  ) => void,
  tintColor: Color,
  textHeight: number,
) {
  const {
    programs,
    uniforms,
    attributes,
    buffers,
    bloomManager,
    offscreenCanvas: offscreenCanvasLocal,
    offscreenCtx: offscreenCtxLocal,
  } = shaderData;

  // Set up WebGL canvas size
  const canvasRect = webglCanvas.getBoundingClientRect();
  const displayWidth = Math.floor(canvasRect.width * window.devicePixelRatio);
  const displayHeight = Math.floor(canvasRect.height * window.devicePixelRatio);
  webglCanvas.width = displayWidth;
  webglCanvas.height = displayHeight;

  // Render terminal to offscreen canvas
  renderTerminalToOffscreen(offscreenCanvasLocal, offscreenCtxLocal, webglCanvas);

  // Setup bloom resources
  const resources = bloomManager.ensure(displayWidth, displayHeight);

  // Step 1: Upload terminal content to original texture
  gl.bindTexture(gl.TEXTURE_2D, resources.originalTexture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, offscreenCanvasLocal);

  // Step 2: Bright pass - extract bright pixels
  gl.bindFramebuffer(gl.FRAMEBUFFER, resources.brightFramebuffer);
  gl.viewport(0, 0, displayWidth, displayHeight);
  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);

  gl.useProgram(programs.brightPassProgram);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, resources.originalTexture);
  gl.uniform1i(uniforms.bright.texture, 0);
  gl.uniform1f(uniforms.bright.threshold, bloomConfig.threshold);
  gl.uniform1i(uniforms.bright.flipY, 1);
  renderFullscreenQuad(gl, attributes, buffers);

  // Step 3: Multi-scale blur passes
  let currentTexture = resources.brightTexture;

  for (let i = 0; i < resources.blurTextures.length; i++) {
    const scale = bloomConfig.scales[i + 1]!;
    const scaledWidth = Math.max(1, Math.floor(displayWidth * scale));
    const scaledHeight = Math.max(1, Math.floor(displayHeight * scale));

    // Horizontal blur pass
    gl.bindFramebuffer(gl.FRAMEBUFFER, resources.tempFramebuffers[i]!);
    gl.viewport(0, 0, scaledWidth, scaledHeight);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    const radiusScale = Math.max(0.3, scale);
    gl.useProgram(programs.blurProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, currentTexture);
    gl.uniform1i(uniforms.blur.texture, 0);
    gl.uniform2f(uniforms.blur.direction, 1.0, 0.0); // Horizontal
    gl.uniform2f(uniforms.blur.resolution, scaledWidth, scaledHeight);
    gl.uniform1f(uniforms.blur.radius, bloomConfig.radius * radiusScale);
    gl.uniform1i(uniforms.blur.flipY, 1);
    renderFullscreenQuad(gl, attributes, buffers);

    // Vertical blur pass
    gl.bindFramebuffer(gl.FRAMEBUFFER, resources.blurFramebuffers[i]!);
    gl.viewport(0, 0, scaledWidth, scaledHeight);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, resources.tempTextures[i]!);
    gl.uniform1i(uniforms.blur.texture, 0);
    gl.uniform2f(uniforms.blur.direction, 0.0, 1.0); // Vertical
    gl.uniform2f(uniforms.blur.resolution, scaledWidth, scaledHeight);
    gl.uniform1f(uniforms.blur.radius, bloomConfig.radius * radiusScale);
    gl.uniform1i(uniforms.blur.flipY, 1);
    renderFullscreenQuad(gl, attributes, buffers);

    // Use this blur result as input for next iteration (if any)
    currentTexture = resources.blurTextures[i]!;
  }

  // Step 4: Final combine pass - render to screen
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, displayWidth, displayHeight);
  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);

  gl.useProgram(programs.combineProgram);

  // Bind original texture
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, resources.originalTexture);
  gl.uniform1i(uniforms.combine.original, 0);

  // Bind bloom textures
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, resources.blurTextures[0] || resources.brightTexture);
  gl.uniform1i(uniforms.combine.bloom1, 1);

  gl.activeTexture(gl.TEXTURE2);
  gl.bindTexture(gl.TEXTURE_2D, resources.blurTextures[1] || resources.brightTexture);
  gl.uniform1i(uniforms.combine.bloom2, 2);

  gl.activeTexture(gl.TEXTURE3);
  gl.bindTexture(gl.TEXTURE_2D, resources.blurTextures[2] || resources.brightTexture);
  gl.uniform1i(uniforms.combine.bloom3, 3);

  gl.uniform1f(uniforms.combine.bloomIntensity, bloomConfig.intensity);

  const TERMINAL_TINT_COLOR = theme[tintColor];
  const HUM_INTENSITY_MAP = {
    [Color.green]: 1.0,
    [Color.amber]: 0.08,
    [Color.cyan]: 0.03,
    [Color.red]: 1.0,
    [Color.white]: 1.0,
    [Color.orange]: 1.0,
    [Color.blue]: 0.1,
  };

  const HUM_INTENSITY = HUM_INTENSITY_MAP[tintColor];

  gl.uniform3f(uniforms.combine.tintColor, TERMINAL_TINT_COLOR[0]!, TERMINAL_TINT_COLOR[1]!, TERMINAL_TINT_COLOR[2]!);
  gl.uniform1f(uniforms.combine.scanlineIntensity, scanlineConfig.intensity);
  gl.uniform1f(uniforms.combine.scanlineFrequency, 0.0); // deprecated, kept for compatibility
  gl.uniform1f(uniforms.combine.movingScanlineIntensity, movingScanlineConfig.intensity);
  gl.uniform1f(
    uniforms.combine.movingScanlineFrequency,
    movingScanlineConfig.frequency / ((textHeight * devicePixelRatio) / (48 * 2)),
  );
  gl.uniform1f(uniforms.combine.movingScanlineSpeed, movingScanlineConfig.speed);
  gl.uniform1f(uniforms.combine.movingScanlineOffset, movingScanlineConfig.offset);
  gl.uniform1f(uniforms.combine.humIntensity, HUM_INTENSITY);
  gl.uniform1f(uniforms.combine.humBars, humBarConfig.bars);
  gl.uniform1f(uniforms.combine.humSpeed, humBarConfig.speed);
  gl.uniform1f(uniforms.combine.humAngleDeg, humBarConfig.angleDeg);
  gl.uniform1f(uniforms.combine.humOffset, humBarConfig.offset);
  gl.uniform1f(uniforms.combine.noiseIntensity, noiseConfig.intensity);
  gl.uniform1f(uniforms.combine.noisePixelSize, devicePixelRatio * (textHeight / 8)); //apple II hardcoded
  gl.uniform1f(uniforms.combine.time, performance.now() * 0.001);
  gl.uniform2f(uniforms.combine.resolution, displayWidth, displayHeight);
  gl.uniform1f(uniforms.combine.curvature, curveConfig.curvature);
  gl.uniform1f(uniforms.combine.vignetteStrength, curveConfig.vignetteStrength);
  gl.uniform1f(uniforms.combine.vignetteSize, curveConfig.vignetteSize);
  gl.uniform1f(uniforms.combine.screenScale, curveConfig.screenScale);
  gl.uniform3f(
    uniforms.combine.bezelColor,
    curveConfig.bezelColor[0]!,
    curveConfig.bezelColor[1]!,
    curveConfig.bezelColor[2]!,
  );
  gl.uniform1f(uniforms.combine.shadowDepth, curveConfig.shadowDepth);
  gl.uniform1f(uniforms.combine.shadowWidth, curveConfig.shadowWidth);
  gl.uniform1f(uniforms.combine.textureIntensity, curveConfig.textureIntensity);

  // Glitch uniforms
  gl.uniform1f(uniforms.combine.glitchIntensity, glitchConfig.intensity);
  gl.uniform1f(uniforms.combine.glitchLineJitter, glitchConfig.lineJitter);
  gl.uniform1f(uniforms.combine.glitchBlock, glitchConfig.block);
  gl.uniform1f(uniforms.combine.glitchFrameJitter, glitchConfig.frameJitter);
  gl.uniform1f(uniforms.combine.glitchDropout, glitchConfig.dropout);
  gl.uniform1f(uniforms.combine.glitchBlockSize, glitchConfig.blockSize);
  gl.uniform1f(uniforms.combine.glitchBurstiness, glitchConfig.burstiness);
  gl.uniform1f(uniforms.combine.glitchSpeed, glitchConfig.speed);

  renderFullscreenQuad(gl, attributes, buffers);
}
